import { useCallback, useState } from 'react';

import { ValidationFunction, InputState, Field, FieldCategory } from '../types';

export const useFormValidation = (fieldDefinitions: FieldCategory[]) => {
  const [formState, setFormState] = useState<Record<FieldCategory, InputState>>(() => {
    const initialState: Record<FieldCategory, InputState> = fieldDefinitions.reduce(
      (acc, key) => {
        acc[key] = { value: '', errorMessage: '' };
        return acc;
      },
      {} as Record<FieldCategory, InputState>
    );

    return initialState;
  });

  const setValue = useCallback((fieldName: FieldCategory, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: { value, errorMessage: '' }
    }));
  }, []);

  const setErrorMessage = useCallback((fieldName: FieldCategory, errorMessage: string) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: { value: prev[fieldName]?.value ?? '', errorMessage }
    }));
  }, []);

  const validate = useCallback(
    (fieldName: FieldCategory, checkValidation: ValidationFunction, ...args: unknown[]) => {
      const currentValue = formState[fieldName]?.value || '';
      const validation = checkValidation(currentValue, ...args);

      setFormState((prev) => ({
        ...prev,
        [fieldName]: { value: prev[fieldName]?.value ?? '', errorMessage: validation.errorMessage ?? '' }
      }));

      return validation.isValid;
    },
    [formState]
  );

  const clearField = useCallback((fieldName: FieldCategory) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: { value: '', errorMessage: '' }
    }));
  }, []);

  // 각 필드에 대한 헬퍼 객체 생성
  const fields = fieldDefinitions.reduce(
    (acc, fieldName) => {
      const fieldState = formState[fieldName] || { value: '', errorMessage: '' };

      acc[fieldName] = {
        inputState: fieldState,
        setValue: (value: string) => setValue(fieldName, value),
        setErrorMessage: (errorMessage: string) => setErrorMessage(fieldName, errorMessage),
        validate: (validationFunction: ValidationFunction, ...args) => validate(fieldName, validationFunction, ...args),
        clear: () => clearField(fieldName),
        isValid: !fieldState.errorMessage
      };

      return acc;
    },
    {} as Record<string, Field>
  );

  return {
    fields
  };
};
